import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Play, Pause, Settings } from 'lucide-react';
import { Howl } from 'howler';
import axios from 'axios';
import BodyScanSettingsPanel from './BodyScanSettingsPanel';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';
import { useBodyScanBackground } from '../hooks/useBodyScanBackground';
import { recordSession } from '../utils/statsTracker';
import { unlockAudio, SILENT_WAV } from '../utils/unlockAudio';
import { startBackgroundSession, stopBackgroundSession } from '../services/backgroundSession';
import AdInterstitial from './AdInterstitial';

const API = process.env.REACT_APP_BACKEND_URL;

const BODY_ZONES = [
  { id: 'feet', name: 'Pieds et orteils', icon: '🦶', instruction: 'Portez votre attention sur vos pieds. Ressentez chaque orteil, la plante de vos pieds, vos talons. Laissez toute tension se dissoudre doucement.', voiceText: 'Concentrez-vous maintenant sur vos pieds. Sentez chaque orteil, la plante de vos pieds. Laissez toute tension fondre doucement.', position: 'bottom' },
  { id: 'calves', name: 'Mollets et genoux', icon: '🦵', instruction: 'Remontez doucement vers vos mollets et vos genoux. Sentez les muscles se détendre, devenir lourds et chauds.', voiceText: 'Remontez doucement vers vos mollets et vos genoux. Sentez ces muscles se relâcher complètement.', position: 'lower' },
  { id: 'thighs', name: 'Cuisses et hanches', icon: '🦵', instruction: 'Votre attention monte vers vos cuisses et vos hanches. Relâchez toute tension accumulée dans cette zone.', voiceText: 'Portez maintenant votre attention sur vos cuisses et vos hanches. Libérez toute tension de cette zone.', position: 'middle-lower' },
  { id: 'belly', name: 'Ventre et bas du dos', icon: '🫁', instruction: 'Concentrez-vous sur votre ventre et votre bas du dos. Respirez profondément, sentez votre abdomen se soulever et s\'abaisser.', voiceText: 'Respirez profondément. Sentez votre ventre se soulever et s\'abaisser. Détendez votre bas du dos.', position: 'middle' },
  { id: 'chest', name: 'Poitrine et haut du dos', icon: '❤️', instruction: 'Portez votre attention sur votre poitrine et votre haut du dos. Sentez votre cœur battre calmement, votre respiration apaisée.', voiceText: 'Sentez votre cœur battre paisiblement. Votre poitrine est légère, votre dos complètement détendu.', position: 'middle-upper' },
  { id: 'hands', name: 'Mains et bras', icon: '🤲', instruction: 'Descendez dans vos bras jusqu\'au bout de vos doigts. Sentez la chaleur, le picotement léger, la détente complète.', voiceText: 'Relâchez vos bras, vos mains, chaque doigt. Sentez une douce chaleur les envahir.', position: 'sides' },
  { id: 'shoulders', name: 'Épaules et nuque', icon: '💆', instruction: 'Relâchez vos épaules, laissez-les tomber naturellement. Détendez votre nuque, libérez toute tension.', voiceText: 'Laissez tomber vos épaules. Relâchez votre nuque. Toute tension s\'évapore.', position: 'upper' },
  { id: 'head', name: 'Visage et tête', icon: '😌', instruction: 'Détendez votre mâchoire, vos joues, votre front. Laissez votre esprit devenir calme et serein. Vous êtes en paix totale.', voiceText: 'Détendez votre visage, votre front, vos yeux. Votre esprit est calme et serein. Vous êtes en paix.', position: 'top' }
];

const VOICE_OPTIONS = {
  custom: { id: 'custom', name: 'Ma voix', isCustom: true, color: 'pink' },
  male: { id: 'male', name: 'Voix Homme', lang: 'fr-FR', pitch: 0.6, rate: 0.65, color: 'blue' },
  synthesis: { id: 'synthesis', name: 'Synthèse', lang: 'fr-FR', pitch: 1.0, rate: 0.8, color: 'purple' },
  female_coach: { id: 'female_coach', name: 'Coach Femme', isCustom: true, color: 'amber' },
  female_tahitian: { id: 'female_tahitian', name: 'Tahitienne', isCustom: true, color: 'emerald' },
  none: { id: 'none', name: 'Sans voix' }
};

const SEGMENT_INTRO_TEXT = "Installez-vous confortablement. Fermez les yeux. Nous allons parcourir votre corps pour une relaxation profonde.";

// Android WebView (Capacitor app) does NOT implement the Web Speech API:
// a bare `speechSynthesis` reference throws ReferenceError and crashes the
// whole React tree (black screen). Always go through this safe handle.
const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;

const BodyScanMode = ({ onClose, isAdmin = false }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentZoneIndex, setCurrentZoneIndex] = useState(0);
  const [zoneProgress, setZoneProgress] = useState(0);
  const [totalDuration, setTotalDuration] = useState(8);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [keepAwake, toggleKeepAwake] = useKeepAwake('bodyscan');
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [bgOpacity, setBgOpacity] = useState(5);
  const [menuTransparency, setMenuTransparency] = useState(40);
  const [selectedVoice, setSelectedVoice] = useState(() => {
    return localStorage.getItem('bodyscan_voice_type') || 'male';
  });

  const handleVoiceChange = (v) => {
    setSelectedVoice(v);
    localStorage.setItem('bodyscan_voice_type', v);
  };
  const [edgeGender, setEdgeGender] = useState(() => localStorage.getItem('bodyscan_edge_gender') || 'female');
  const handleEdgeGenderChange = (g) => {
    setEdgeGender(g);
    localStorage.setItem('bodyscan_edge_gender', g);
  };
  const edgeVoicesRef = useRef({});
  const [voiceVolume, setVoiceVolume] = useState(1.0);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [transitionSoundEnabled, setTransitionSoundEnabled] = useState(true);
  const [customVoices, setCustomVoices] = useState({ custom: {}, male: {} });
  const [uploadingZone, setUploadingZone] = useState(null);
  const [uploadingVoice, setUploadingVoice] = useState(null);
  const [lockedVoices, setLockedVoices] = useState(() => {
    try { return JSON.parse(localStorage.getItem('locked_bodyscan_voices') || '{}'); } catch { return {}; }
  });

  const toggleLockVoice = (key) => {
    setLockedVoices(prev => {
      const upd = { ...prev, [key]: !prev[key] };
      localStorage.setItem('locked_bodyscan_voices', JSON.stringify(upd));
      return upd;
    });
  };

  const videoRef = useRef(null);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  const zoneStartTimeRef = useRef(null);
  const lastSpokenZone = useRef(-1);
  const introActiveRef = useRef(false);
  const transitionSoundRef = useRef(null);
  const bgSessionRef = useRef(false);
  const endBgSession = useCallback(() => {
    if (bgSessionRef.current) { bgSessionRef.current = false; stopBackgroundSession(); }
  }, []);
  useEffect(() => () => {
    if (bgSessionRef.current) { bgSessionRef.current = false; stopBackgroundSession(); }
  }, []);

  // Refs for stable closures in animation loop
  const customVoicesRef = useRef(customVoices);
  const selectedVoiceRef = useRef(selectedVoice);
  const voiceVolumeRef = useRef(voiceVolume);
  useEffect(() => { customVoicesRef.current = customVoices; }, [customVoices]);
  useEffect(() => { selectedVoiceRef.current = selectedVoice; }, [selectedVoice]);
  useEffect(() => { voiceVolumeRef.current = voiceVolume; }, [voiceVolume]);

  // Single persistent <audio> element for ALL voice segments.
  // Android WebView blocks audio elements created outside a user gesture
  // (zone voices start from timers/RAF): one element is "primed" during the
  // Start click, then reused for intro/zones/outro — always allowed to play.
  const voiceElRef = useRef(null);
  const ensureVoiceEl = useCallback(() => {
    if (!voiceElRef.current) {
      const el = new window.Audio();
      el.preload = 'auto';
      voiceElRef.current = el;
    }
    return voiceElRef.current;
  }, []);
  const playVoiceUrl = useCallback((url, onEnd) => {
    const el = ensureVoiceEl();
    el.onended = onEnd || null;
    el.onerror = onEnd || null;
    el.muted = false;
    el.volume = voiceVolumeRef.current;
    el.src = url;
    const p = el.play();
    if (p && p.catch) p.catch(() => { if (onEnd) onEnd(); });
  }, [ensureVoiceEl]);
  const stopVoice = useCallback(() => {
    const el = voiceElRef.current;
    if (el) { el.onended = null; el.onerror = null; el.pause(); }
  }, []);

  // Custom hook for backgrounds
  const bg = useBodyScanBackground();

  const currentZone = BODY_ZONES[currentZoneIndex];
  const zoneDuration = (totalDuration * 60) / BODY_ZONES.length;

  // Fetch data on mount
  useEffect(() => { bg.fetchBackgroundData(); }, []);

  // Edge TTS natural voices for "Synthèse"
  useEffect(() => {
    if (selectedVoice !== 'synthesis') return;
    const items = [
      ...BODY_ZONES.map(z => ({ id: z.id, text: z.voiceText })),
      { id: 'intro', text: SEGMENT_INTRO_TEXT },
      { id: 'outro', text: 'La séance est terminée. Prenez le temps de revenir doucement. Vous êtes complètement relaxé.' }
    ];
    axios.post(`${API}/api/edge-tts/generate`, { items, voice: edgeGender })
      .then(res => { edgeVoicesRef.current = res.data.voices || {}; })
      .catch(err => console.error('Edge TTS error:', err));
  }, [selectedVoice, edgeGender]);

  // Fetch custom voices

  useEffect(() => {
    const fetchCustomVoices = async () => {
      try {
        const response = await axios.get(`${API}/api/bodyscan-voices`);
        const voicesMap = {};
        response.data.forEach(v => {
          const voiceType = v.voice_type || 'custom';
          if (!voicesMap[voiceType]) voicesMap[voiceType] = {};
          voicesMap[voiceType][v.zone] = v.audio_url;
        });
        setCustomVoices(voicesMap);
      } catch (error) { console.error('Error fetching custom voices:', error); }
    };
    fetchCustomVoices();
  }, []);

  // Load speech synthesis voices
  useEffect(() => {
    if (!synth) return;
    const loadVoices = () => {
      const voices = synth.getVoices();
      setAvailableVoices(voices.filter(v => v.lang.startsWith('fr')));
    };
    loadVoices();
    synth.onvoiceschanged = loadVoices;
    return () => { synth.cancel(); };
  }, []);

  // Preload transition sound (WebAudio path: unlocked globally by unlockAudio)
  useEffect(() => {
    transitionSoundRef.current = new Howl({
      src: ['https://freesound.org/data/previews/339/339810_5121236-lq.mp3'],
      html5: false, volume: 0.4
    });
    return () => {
      if (transitionSoundRef.current) transitionSoundRef.current.unload();
      const el = voiceElRef.current;
      if (el) { el.onended = null; el.onerror = null; el.pause(); el.removeAttribute('src'); }
    };
  }, []);

  const playCustomVoice = useCallback((zoneId, voiceType) => {
    const vt = voiceType || selectedVoiceRef.current;
    const voiceTypeVoices = customVoicesRef.current[vt] || {};
    const audioUrl = voiceTypeVoices[zoneId];
    if (!audioUrl) return false;
    let fullUrl;
    if (audioUrl.startsWith('http')) fullUrl = audioUrl;
    else if (audioUrl.startsWith('/api/')) fullUrl = `${API}${audioUrl}`;
    else fullUrl = audioUrl; // /voices/... served by frontend directly
    playVoiceUrl(fullUrl);
    return true;
  }, [playVoiceUrl]);

  const speak = useCallback((text, zoneId = null) => {
    const voice = selectedVoiceRef.current;
    if (voice === 'none') return;

    // Edge TTS natural voice for "Synthèse"
    if (voice === 'synthesis' && zoneId && edgeVoicesRef.current[zoneId]) {
      playVoiceUrl(`${API}${edgeVoicesRef.current[zoneId]}`);
      return;
    }
    
    // Try custom/uploaded voice first
    const voiceTypeVoices = customVoicesRef.current[voice] || {};
    if (zoneId && voiceTypeVoices[zoneId]) {
      playCustomVoice(zoneId, voice);
      return;
    }
    
    // For non-synthesis types without uploaded voice, skip silently
    if (voice === 'custom' || voice === 'female_tahitian' || voice === 'female_coach') return;
    
    // Fallback to speech synthesis for male/synthesis
    if (!text || !synth) return;
    synth.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'fr-FR';
    utterance.volume = voiceVolumeRef.current;
    const voiceConfig = VOICE_OPTIONS[voice];
    if (voiceConfig?.pitch) utterance.pitch = voiceConfig.pitch;
    if (voiceConfig?.rate) utterance.rate = voiceConfig.rate;
    const voices = synth.getVoices();
    const frenchVoices = voices.filter(v => v.lang.startsWith('fr'));
    if (voice === 'male') {
      const maleVoice = frenchVoices.find(v =>
        v.name.toLowerCase().includes('male') || v.name.toLowerCase().includes('homme') ||
        v.name.includes('Thomas') || v.name.includes('Nicolas') || v.name.includes('Google')
      ) || frenchVoices[frenchVoices.length - 1] || frenchVoices[0];
      if (maleVoice) utterance.voice = maleVoice;
    }
    synth.speak(utterance);
  }, [playCustomVoice]);

  const handleVoiceUpload = async (zoneId, file, voiceType = selectedVoice) => {
    if (!file) return;
    setUploadingZone(zoneId);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('zone', zoneId);
    formData.append('voice_type', voiceType);
    try {
      const response = await axios.post(`${API}/api/bodyscan-voices/upload`, formData);
      setCustomVoices(prev => ({ ...prev, [voiceType]: { ...(prev[voiceType] || {}), [zoneId]: response.data.audio_url } }));
    } catch (error) {
      console.error('Error uploading voice:', error);
      alert('Erreur lors de l\'upload du fichier audio');
    } finally { setUploadingZone(null); }
  };

  const handleDeleteVoice = async (zoneId, voiceType = selectedVoice) => {
    try {
      await axios.delete(`${API}/api/bodyscan-voices/${voiceType}/${zoneId}`);
      setCustomVoices(prev => {
        const n = { ...prev };
        if (n[voiceType]) { const t = { ...n[voiceType] }; delete t[zoneId]; n[voiceType] = t; }
        return n;
      });
    } catch (error) { console.error('Error deleting voice:', error); }
  };

  const playTransitionSound = useCallback(() => {
    if (transitionSoundEnabled && transitionSoundRef.current) {
      transitionSoundRef.current.stop();
      transitionSoundRef.current.play();
    }
  }, [transitionSoundEnabled]);

  // Presentation played BEFORE the session starts (no audio overlap)
  const playIntro = useCallback((onDone) => {
    const voice = selectedVoiceRef.current;
    if (voice === 'none') { onDone(); return; }
    let finished = false;
    const done = () => { if (!finished) { finished = true; onDone(); } };
    // Safety net: never block the session more than 30s
    setTimeout(done, 30000);
    const playHowlIntro = (url) => {
      let fullUrl;
      if (url.startsWith('http')) fullUrl = url;
      else if (url.startsWith('/api/')) fullUrl = `${API}${url}`;
      else fullUrl = url;
      playVoiceUrl(fullUrl, done);
    };
    // 1) Coach-uploaded intro for the selected voice type
    const customIntro = (customVoicesRef.current[voice] || {})['intro'];
    if (customIntro) { playHowlIntro(customIntro); return; }
    // 2) Edge TTS natural intro (Synthèse) — generate on the fly if not ready yet
    if (voice === 'synthesis') {
      if (edgeVoicesRef.current['intro']) {
        playHowlIntro(edgeVoicesRef.current['intro']);
        return;
      }
      const gender = localStorage.getItem('bodyscan_edge_gender') || 'female';
      axios.post(`${API}/api/edge-tts/generate`, { items: [{ id: 'intro', text: SEGMENT_INTRO_TEXT }], voice: gender })
        .then(res => {
          const url = (res.data.voices || {}).intro;
          if (url && introActiveRef.current && !finished) {
            edgeVoicesRef.current = { ...edgeVoicesRef.current, intro: url };
            playHowlIntro(url);
          } else { done(); }
        })
        .catch(done);
      return;
    }
    // 3) Custom voice types without an uploaded intro: skip straight to the session
    if (voice === 'custom' || voice === 'female_tahitian' || voice === 'female_coach') { done(); return; }
    // 4) Browser speech synthesis fallback (male / synthesis without edge)
    try {
      if (!synth || synth.getVoices().length === 0) { done(); return; }
      const frenchVoices = synth.getVoices().filter(v => v.lang.startsWith('fr'));
      synth.cancel();
      const utterance = new SpeechSynthesisUtterance(SEGMENT_INTRO_TEXT);
      utterance.lang = 'fr-FR';
      utterance.volume = voiceVolumeRef.current;
      const voiceConfig = VOICE_OPTIONS[voice];
      if (voiceConfig?.pitch) utterance.pitch = voiceConfig.pitch;
      if (voiceConfig?.rate) utterance.rate = voiceConfig.rate;
      if (voice === 'male') {
        const maleVoice = frenchVoices.find(v =>
          v.name.toLowerCase().includes('male') || v.name.toLowerCase().includes('homme') ||
          v.name.includes('Thomas') || v.name.includes('Nicolas') || v.name.includes('Google')
        ) || frenchVoices[frenchVoices.length - 1] || frenchVoices[0];
        if (maleVoice) utterance.voice = maleVoice;
      }
      utterance.onend = done;
      utterance.onerror = done;
      synth.speak(utterance);
    } catch { done(); }
  }, []);

  // Animation loop — RAF for smooth visuals + 1s interval fallback so the
  // session keeps progressing when the screen is locked (RAF freezes then).
  useEffect(() => {
    if (!isRunning) return;
    const animate = (timestamp) => {
      // Idempotent scheduling: cancel any pending frame before running so the
      // background interval can also drive this loop without duplication
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      // Wait for the presentation to finish before starting the session clock
      if (introActiveRef.current) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
        zoneStartTimeRef.current = timestamp;
        playTransitionSound();
        if (lastSpokenZone.current !== 0) {
          speak(BODY_ZONES[0].voiceText, BODY_ZONES[0].id);
          lastSpokenZone.current = 0;
        }
      }
      const totalElapsed = (timestamp - startTimeRef.current) / 1000;
      setElapsedTime(totalElapsed);
      if (totalElapsed >= totalDuration * 60) {
        setIsRunning(false);
        recordSession('bodyscan', totalElapsed);
        speak("La séance est terminée. Prenez le temps de revenir doucement. Vous êtes complètement relaxé.", 'outro');
        playTransitionSound();
        setShowInterstitial(true);
        return;
      }
      const zoneElapsed = (timestamp - zoneStartTimeRef.current) / 1000;
      setZoneProgress(Math.min((zoneElapsed / zoneDuration) * 100, 100));
      if (zoneElapsed >= zoneDuration && currentZoneIndex < BODY_ZONES.length - 1) {
        const nextIndex = currentZoneIndex + 1;
        setCurrentZoneIndex(nextIndex);
        zoneStartTimeRef.current = timestamp;
        playTransitionSound();
        if (lastSpokenZone.current !== nextIndex) {
          setTimeout(() => { speak(BODY_ZONES[nextIndex].voiceText, BODY_ZONES[nextIndex].id); }, 1000);
          lastSpokenZone.current = nextIndex;
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    const bgTick = setInterval(() => {
      if (document.hidden) animate(performance.now());
    }, 1000);
    return () => {
      clearInterval(bgTick);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isRunning, currentZoneIndex, zoneDuration, totalDuration, playTransitionSound, speak]);

  const handleStart = () => {
    // Unlock audio synchronously inside the user gesture:
    // 1) Howler WebAudio context (transition sound)
    unlockAudio();
    // 2) Prime the persistent voice <audio> element with a silent clip so the
    //    Android WebView keeps allowing play() calls made later by timers/RAF.
    const el = ensureVoiceEl();
    el.muted = true;
    el.src = SILENT_WAV;
    const p = el.play();
    if (p && p.catch) p.catch(() => {});
    // Keep the session alive when the screen locks (foreground service on app)
    bgSessionRef.current = true;
    startBackgroundSession('Scan corporel en cours');
    setIsRunning(true); setCurrentZoneIndex(0); setZoneProgress(0); setElapsedTime(0);
    startTimeRef.current = null; zoneStartTimeRef.current = null; lastSpokenZone.current = -1;
    // Play the presentation first; the session (timer + zone 1) starts when it ends
    introActiveRef.current = true;
    playIntro(() => { introActiveRef.current = false; });
  };

  const handleStop = () => {
    if (isRunning && elapsedTime > 0) {
      recordSession('bodyscan', elapsedTime);
    }
    introActiveRef.current = false;
    setIsRunning(false); if (synth) synth.cancel();
    stopVoice();
    endBgSession();
    if (animationRef.current) cancelAnimationFrame(animationRef.current);
  };

  const handleClose = () => {
    introActiveRef.current = false;
    if (synth) synth.cancel();
    stopVoice();
    endBgSession();
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      className="fixed inset-0 z-[200] mode-enter"
      style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #4c1d95 100%)',
        opacity: 1,
      }}
      data-testid="body-scan-mode"
    >
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden z-0">
        {bg.isImageMode && bg.currentImage ? (
          <img key={bg.currentImage.id}
            src={bg.currentImage.url?.startsWith('http') ? bg.currentImage.url : `${API}${bg.currentImage.url}`}
            alt={bg.currentImage.name}
            className={`w-full h-full object-cover ${bg.getAnimationClass(bg.currentImage.animation)}`}
            style={{ filter: 'brightness(0.95) saturate(1.0)' }}
            fetchPriority="high"
            loading="eager"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          /> 
        ) : bg.currentBg ? (
          <video ref={videoRef} key={bg.currentBg?.id || 'default'}
            autoPlay loop muted playsInline preload="auto"
            className="w-full h-full object-cover animate-video-fade-in"
            style={{ filter: 'brightness(0.95) saturate(1.1)' }}
            onLoadedData={(e) => { e.target.style.opacity = '1'; }}
            src={bg.currentBg?.url?.startsWith('http') ? bg.currentBg.url : `${API}${bg.currentBg.url}`} />
        ) : null}
        <div
          className="absolute inset-0 bg-black transition-opacity"
          style={{ opacity: Math.min(bgOpacity, 60) / 100 }}
        />
      </div>

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 px-4 pt-10 pb-4 flex items-center justify-between z-20">
        <button onClick={handleClose}
          className="p-3 rounded-full backdrop-blur-sm text-white transition-all"
          style={{ backgroundColor: `rgba(0,0,0,${menuTransparency / 100})` }}
          data-testid="close-body-scan">
          <X className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-bodyscan"
            className="px-3.5 py-2.5 rounded-full backdrop-blur-sm text-white text-xs font-medium hover:bg-white/10"
            style={{ backgroundColor: `rgba(0,0,0,${menuTransparency / 100})` }} />
          <button onClick={() => setShowSettingsPanel(!showSettingsPanel)}
            className={`px-5 py-2.5 rounded-full backdrop-blur-sm text-white text-sm font-medium transition-all flex items-center gap-2 ${
              showSettingsPanel ? 'bg-gradient-to-r from-indigo-500/50 to-purple-500/50 border border-white/30' : 'hover:bg-white/10'
            }`}
            style={{ backgroundColor: showSettingsPanel ? undefined : `rgba(0,0,0,${menuTransparency / 100})` }}
            data-testid="settings-panel-btn-scan">
            <Settings className="w-4 h-4" />
            <span className="whitespace-nowrap"><span className="hidden sm:inline">Paramétrer votre séance</span><span className="sm:hidden">Paramétrer</span></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="absolute inset-0 z-10 overflow-y-auto px-4 pt-28 pb-56 flex flex-col">
        <div className="max-w-4xl w-full mx-auto flex flex-col items-center my-auto">
          <div className="max-w-md w-full mb-4 px-4" data-testid="body-scan-advice-text">
            <p className="text-white/90 text-sm sm:text-base leading-relaxed text-center italic font-body px-2" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.9)' }}>
              Allongez-vous confortablement et fermez les yeux : en visualisant chaque partie de votre corps qui se détend, vous relâchez les tensions profondes, apaisez votre mental et favorisez un lâcher-prise total.
            </p>
          </div>
          <div className="w-full text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="text-4xl">{currentZone.icon}</span>
              <h2 className="text-white text-2xl sm:text-3xl font-heading font-semibold">{currentZone.name}</h2>
            </div>
            <div className="w-full bg-white/10 rounded-full h-2 mb-6">
              <div className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full transition-all duration-300"
                style={{ width: `${zoneProgress}%` }} />
            </div>
            <div className="bg-black/30 backdrop-blur-sm rounded-2xl p-4 sm:p-6 mb-4">
              <p className="text-white/90 text-lg sm:text-xl leading-relaxed font-body">
                {isRunning ? currentZone.instruction : 'Installez-vous confortablement, fermez les yeux et laissez-vous guider à travers un voyage de relaxation profonde...'}
              </p>
            </div>
            <div className="flex items-center justify-center gap-8 text-white/70 mb-2">
              <div className="text-center">
                <p className="text-2xl font-heading text-white">{currentZoneIndex + 1}/{BODY_ZONES.length}</p>
                <p className="text-sm">Zone</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading text-white">{formatTime(elapsedTime)}</p>
                <p className="text-sm">Temps</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-heading text-white">{totalDuration}:00</p>
                <p className="text-sm">Durée</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Controls */}
      <div className="absolute bottom-0 left-0 right-0 pt-6 pb-24 px-6 flex flex-col items-center gap-3 z-20">
        <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm rounded-full px-4 py-2">
          <span className="text-white/60 text-sm">Durée:</span>
          {[5, 8, 12].map((mins) => (
            <button key={mins} onClick={() => setTotalDuration(mins)}
              className={`px-3 py-1 rounded-full text-sm transition-all ${
                totalDuration === mins ? 'bg-white text-slate-900' : 'text-white/70 hover:text-white'
              }`}>
              {mins}min
            </button>
          ))}
        </div>
        {!isRunning ? (
          <button onClick={handleStart}
            className="px-8 py-4 rounded-full bg-white text-slate-900 font-semibold text-lg hover:scale-105 transition-transform shadow-xl flex items-center gap-2"
            data-testid="start-body-scan">
            <Play className="w-6 h-6" /> Commencer le scan
          </button>
        ) : (
          <button onClick={handleStop}
            className="px-8 py-4 rounded-full bg-white/20 backdrop-blur-sm text-white font-semibold text-lg hover:bg-white/30 transition-all flex items-center gap-2"
            data-testid="stop-body-scan">
            <Pause className="w-6 h-6" /> Pause
          </button>
        )}
      </div>

      {/* Settings Panel */}
      <BodyScanSettingsPanel
        isOpen={showSettingsPanel}
        onClose={() => setShowSettingsPanel(false)}
        bgOpacity={bgOpacity}
        onBgOpacityChange={setBgOpacity}
        menuTransparency={menuTransparency}
        onMenuTransparencyChange={setMenuTransparency}
        voiceType={selectedVoice}
        onVoiceTypeChange={handleVoiceChange}
        edgeGender={edgeGender}
        onEdgeGenderChange={handleEdgeGenderChange}
        voiceVolume={voiceVolume}
        onVoiceVolumeChange={setVoiceVolume}
        customVoices={customVoices}
        onVoiceUpload={handleVoiceUpload}
        uploadingVoice={uploadingVoice}
        bodyParts={[{ id: 'intro', name: '🎙️ Introduction (début de séance)' }, ...BODY_ZONES]}
        isAdmin={isAdmin}
        lockedVoices={lockedVoices}
        onToggleLockVoice={toggleLockVoice}
        onDeleteVoice={handleDeleteVoice}
        backgrounds={bg.customVideos}
        customVideos={bg.customVideos}
        selectedBg={bg.selectedBg}
        onBgSelect={(idx) => { bg.setSelectedBg(idx); bg.setSelectedImageBg(null); }}
        backgroundImages={bg.backgroundImages}
        selectedImageBg={bg.selectedImageBg}
        onImageBgSelect={bg.setSelectedImageBg}
        hoveredVideo={bg.hoveredVideo}
        onHoverVideo={bg.setHoveredVideo}
        onVideoUpload={bg.handleVideoUpload}
        onVideoRename={bg.handleVideoRename}
        onVideoToggleLock={bg.handleVideoToggleLock}
        onVideoDelete={bg.handleVideoDelete}
        defaultVideoId={bg.defaultVideoId}
        onSetDefaultVideo={bg.handleSetDefaultVideo}
        uploadingVideo={bg.uploadingVideo}
        onImageUpload={async (file) => {
          bg.setUploadingImage(true);
          const formData = new FormData();
          formData.append('file', file);
          formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
          formData.append('animation', bg.selectedAnimation);
          try {
            const response = await axios.post(API + '/api/breathing-images/upload', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
            bg.backgroundImages.push(response.data);
          } catch (error) { console.error('Error uploading image:', error); }
          bg.setUploadingImage(false);
        }}
        uploadingImage={bg.uploadingImage}
        selectedAnimation={bg.selectedAnimation}
        onAnimationChange={bg.setSelectedAnimation}
        defaultImageId={bg.defaultImageId}
        onSetDefaultImage={bg.handleSetDefaultImage}
        onImageRename={bg.handleImageRename}
        onImageToggleLock={bg.handleImageToggleLock}
        onImageDelete={bg.handleImageDelete}
      />
      <AdInterstitial show={showInterstitial} onClose={() => setShowInterstitial(false)} />
    </div>
  );
};

export default BodyScanMode;
